import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { FirmOfferDetailDto } from '../types/salesOrder.types';

interface Props {
  firmOffer: FirmOfferDetailDto;
}

// FO-SOI-3: read-only chip block showing the fields the SO inherits from the FO.
// Mirrors what the NetSuite UI's transform-from-estimate route pre-fills.
export function FoSummaryCard({ firmOffer }: Props) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          Inherited from {firmOffer.tranId}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Customer" value={firmOffer.customer.refName} />
        <Field label="Head / Main Office" value={firmOffer.headMainOffice?.refName} />
        <Field label="Project" value={firmOffer.projectName} />
        <Field label="Customer PO #" value={firmOffer.customerPoNumber} />
        <Field label="Contract Expiry" value={firmOffer.contractExpiryDate} />
        <Field label="Terms" value={firmOffer.terms?.refName} />
        <Field label="Salesman" value={firmOffer.salesRep?.refName} />
        <Field label="Sales Coordinator" value={firmOffer.salesCoordinator?.refName} />
        <Field label="Freight per Ton" value={
          firmOffer.freightChargePerTon ? `₱${firmOffer.freightChargePerTon.toFixed(2)}` : null
        } />
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Waived Freight</span>
          {firmOffer.waivedFreight ? (
            <Badge variant={firmOffer.waivedFreight.refName === 'YES' ? 'destructive' : 'secondary'}>
              {firmOffer.waivedFreight.refName}
            </Badge>
          ) : <span className="text-muted-foreground">—</span>}
        </div>
        <LinkField label="Form 2303 Link" url={firmOffer.form2303LinkUrl} />
        <LinkField label="PO Link" url={firmOffer.poLinkUrl} />
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="font-medium">{value || <span className="text-muted-foreground">—</span>}</span>
    </div>
  );
}

function LinkField({ label, url }: { label: string; url: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-info hover:underline"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
